import {
	Button,
	DatePicker,
	FlexColumn,
	FlexRow,
	Modal,
	NumberField,
	Scrollbar,
	Table,
	Text,
	Tooltip,
	EMPHASIS,
	BORDER,
	Pill,
	PillVariant,
	SubHeading,
} from '@forecast-it/design-system';
import {DateValue} from '@internationalized/date';
import {AddCurrencyRateRow} from './AddCurrencyRateRow';
import moment from 'moment-timezone';
import React, {useEffect, useRef, useState} from 'react';
import {useIntl, FormattedMessage} from 'react-intl';
import {currencies, DATE_FORMAT_DAY, DATE_FORMAT_DAY_DISPLAY} from '../../../constants';
import CreateDateEffectiveExchangeRatesMutation from '../../../mutations/CreateExchangeRateBatchMutation';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {createToast} from '../../shared/components/toasts/toast';
import Util from '../../shared/util/util';
import type {ExchangeRate, RateStatus} from './types';
import {getRateStatus} from './ExchangeRateUtil';

type UnifiedRate = ExchangeRate & {isDraft: boolean};

const ROW_FLEX_STYLE = {display: 'flex', alignItems: 'center', gap: '8px'} as const;
const NUMBER_FIELD_WRAPPER_STYLE = {width: '230px'} as const;

type Company = {
	id: string;
	currency: string;
	name?: string;
	timezone?: string;
};

export type CurrencyHistoryModalPayload = {
	currencyCode: string;
	exchangeRates: ExchangeRate[];
	company: Company;
	onRateAdded?: () => void;
	onDelete?: () => void;
};

export const showCurrencyHistoryModal = (props: CurrencyHistoryModalPayload): void => {
	showModal({
		type: MODAL_TYPE.CURRENCY_HISTORY,
		...props,
	});
};

interface Props {
	currencyCode: string;
	exchangeRates: ExchangeRate[];
	company: Company;
	onRateAdded?: () => void;
	onDelete?: () => void;
	closeModal: () => void;
}

type PendingRate = {startDate: string; rate: number; createdAt: string};

const STATUS_VARIANTS: Record<RateStatus, PillVariant> = {
	current: PillVariant.SUCCESS,
	past: PillVariant.NEUTRAL,
};

export const CurrencyHistoryModal = ({currencyCode, exchangeRates, company, onRateAdded, onDelete, closeModal}: Props) => {
	const {formatMessage} = useIntl();
	const [showAddRateForm, setShowAddRateForm] = useState(false);
	const [pendingRates, setPendingRates] = useState<PendingRate[]>([]);
	const [formStartDate, setFormStartDate] = useState<string | undefined>(undefined);
	const [formRate, setFormRate] = useState<number | undefined>(undefined);
	const [showUnsavedGuard, setShowUnsavedGuard] = useState(false);
	const scrollbarRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			if (scrollbarRef.current) {
				scrollbarRef.current.scrollTop = scrollbarRef.current.scrollHeight;
			}
		});
		return () => cancelAnimationFrame(frame);
	}, []);

	const currencyEntry = currencies.find(c => c.code === currencyCode);
	const currencyLabel = currencyEntry
		? `${currencyEntry.code} (${currencyEntry.symbol}) - ${currencyEntry.name}`
		: currencyCode;

	const tz = company.timezone ?? 'UTC';
	const [companyNow, setCompanyNow] = useState(() => moment().tz(tz));
	useEffect(() => {
		const id = setInterval(() => setCompanyNow(moment().tz(tz)), 60_000);
		return () => clearInterval(id);
	}, [tz]);
	const companyTimeLabel = `${companyNow.format('DD MMM YYYY, LT')} ${Util.getTimeZoneLabel(companyNow.toDate(), tz)}`;

	const today = moment().tz(tz);
	const dateToCompare = today.format(DATE_FORMAT_DAY);

	// Compute minValue: one day after the latest known start date across persisted + pending rates
	const allKnownStartDates = [
		...exchangeRates.filter(r => r.startDate !== null).map(r => r.startDate as string),
		...pendingRates.map(p => p.startDate),
	];
	const latestKnownStartDate = allKnownStartDates.sort().pop() ?? null;
	const minDateValue = latestKnownStartDate ? moment(latestKnownStartDate).add(1, 'days').format(DATE_FORMAT_DAY) : undefined;

	// Check if there is an exchange rate available and today and hence no dates available for setting rates
	const anyDatesAvailable = minDateValue === undefined || minDateValue <= dateToCompare;

	const unifiedRates: UnifiedRate[] = [
		...exchangeRates.map(r => ({...r, rate: r.rate ?? 0, isDraft: false})),
		...pendingRates.map((p, i) => ({
			id: `draft-${i}`,
			startDate: p.startDate,
			rate: p.rate,
			currency: currencyCode,
			createdAt: p.createdAt,
			endDate: null,
			isUsed: null,
			isDraft: true,
		})),
	];

	const sortedRates = [...unifiedRates].sort((a, b) => {
		if (!a.startDate) return -1;
		if (!b.startDate) return 1;
		return a.startDate.localeCompare(b.startDate);
	});

	const currentRate = sortedRates.filter(r => r.startDate === null || r.startDate <= dateToCompare).pop();
	const isInvalidRate = formRate !== undefined && formRate <= 0;
	const isDuplicateRate = formRate !== undefined && currentRate !== undefined && currentRate.rate === formRate;

	const historyRows = sortedRates.map((item, sortedIndex) => {
		const nextRate = sortedRates[sortedIndex + 1] ?? null;
		const effectiveEndDate = nextRate?.startDate
			? moment(nextRate.startDate).subtract(1, 'days').format(DATE_FORMAT_DAY)
			: null;

		const status: RateStatus = getRateStatus({...item, endDate: effectiveEndDate}, today);

		const startLabel = item.startDate
			? moment(item.startDate).format(DATE_FORMAT_DAY_DISPLAY)
			: formatMessage({id: 'settings_finance.currencies.history_modal.initial'});

		const endLabel = effectiveEndDate
			? moment(effectiveEndDate).format(DATE_FORMAT_DAY_DISPLAY)
			: formatMessage({id: 'settings_finance.currencies.history_modal.onwards'});

		return {
			id: item.id,
			effectiveFrom: `${startLabel} - ${endLabel}`,
			startLabel,
			endLabel,
			rateFormatted: `${item.rate} ${item.currency}`,
			createdAtFormatted: Util.formatUtcDateTimeToLocalTimezone(item.createdAt),
			status,
		};
	});

	const resetForm = () => {
		setFormStartDate(undefined);
		setFormRate(undefined);
		setShowAddRateForm(false);
	};

	const handleConfirmAddRate = () => {
		if (!formStartDate || formRate === undefined || formRate <= 0) return;
		setPendingRates(prev => [...prev, {startDate: formStartDate, rate: formRate, createdAt: new Date().toISOString()}]);
		resetForm();
		createToast({
			duration: 2000,
			message: formatMessage({id: 'settings_finance.currencies.history_modal.rate_added_toast'}),
		});
	};

	const handleSave = () => {
		Util.CommitMutation(
			CreateDateEffectiveExchangeRatesMutation,
			{
				companyId: company.id,
				currency: currencyCode,
				rates: pendingRates.map(({startDate, rate}) => ({startDate, rate})),
			},
			() => {
				closeModal();
				onRateAdded?.();
			}
		);
	};

	const handleClose = () => {
		if (pendingRates.length > 0) {
			setShowUnsavedGuard(true);
		} else {
			closeModal();
		}
	};

	return (
		<>
			<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={handleClose}>
				<Modal.Title>{formatMessage({id: 'settings_finance.currencies.history_modal.title'})}</Modal.Title>
				<Modal.Content>
					<FlexColumn gap="m">
						<FlexRow justifyContent="space-between" alignItems="center">
							<Text>{currencyLabel}</Text>
							<Text color="black">
								{formatMessage(
									{id: 'settings_finance.currencies.company_date_time_label'},
									{
										companyName: company.name,
										companyTimeLabel,
										b: (chunks: React.ReactNode) => <b>{chunks}</b>,
									}
								)}
							</Text>
						</FlexRow>
						<Scrollbar ref={scrollbarRef} maxHeight={360} hasFocusableContent>
							<Table width="100%" data={historyRows} stickyHeader>
								<Table.TextColumn
									accessorKey="_"
									header={formatMessage({id: 'settings_finance.currencies.history_modal.effective_from'})}
									icon={(row: {startLabel: string; endLabel: string}) => (
										<>
											<SubHeading size={2}>{`${row.startLabel} `}</SubHeading>
											{`- ${row.endLabel}`}
										</>
									)}
								/>
								<Table.TextColumn
									accessorKey="_"
									header={formatMessage({id: 'common.rate'})}
									icon={(row: {rateFormatted: string}) => (
										<SubHeading size={2}>{row.rateFormatted}</SubHeading>
									)}
								/>
								<Table.TextColumn
									accessorKey="createdAtFormatted"
									header={formatMessage({id: 'global_filter_field.created_at'})}
								/>
								<Table.TextColumn
									accessorKey="statusIcon"
									header={formatMessage({id: 'settings_finance.currencies.history_modal.status'})}
									icon={(row: {status: RateStatus}) => (
										<Pill variant={STATUS_VARIANTS[row.status]}>
											{formatMessage({
												id: `settings_finance.currencies.history_modal.status_${row.status}`,
											})}
										</Pill>
									)}
								/>
							</Table>
						</Scrollbar>

						{showAddRateForm ? (
							<AddCurrencyRateRow
								onConfirm={handleConfirmAddRate}
								onCancel={resetForm}
								isConfirmDisabled={!formStartDate || formRate === undefined || isInvalidRate}
							>
								<div style={ROW_FLEX_STYLE}>
									<DatePicker
										value={formStartDate}
										minValue={minDateValue}
										isDateUnavailable={(date: DateValue) => date.toString() > dateToCompare}
										onChange={setFormStartDate}
									/>
									{(() => {
										const hasError = isInvalidRate || isDuplicateRate;
										const field = (
											<div style={NUMBER_FIELD_WRAPPER_STYLE}>
												<NumberField
													value={formRate}
													onChange={setFormRate}
													prefixText={`1 ${company.currency} =`}
													suffixText={currencyCode}
													align={NumberField.ALIGN.RIGHT}
													maximumFractionDigits={4}
													boldValue
													required
													hasError={hasError}
												/>
											</div>
										);
										return hasError ? (
											<Tooltip
												interactable={true}
												content={formatMessage({
													id: isDuplicateRate
														? 'settings_finance.currencies.history_modal.error_duplicate_rate'
														: 'settings_finance.currencies.history_modal.error_negative_rate',
												})}
												width={Tooltip.WIDTH.l}
											>
												{field}
											</Tooltip>
										) : (
											field
										);
									})()}
								</div>
							</AddCurrencyRateRow>
						) : (
							<Button
								emphasis="low"
								icon="plus"
								onPress={() => setShowAddRateForm(true)}
								disabled={!anyDatesAvailable}
							>
								{formatMessage({id: 'settings_finance.currencies.history_modal.add_new_rate'})}
							</Button>
						)}

						<FlexRow justifyContent={'space-between'} alignItems={'center'}>
							<FlexColumn>
								<FlexRow gap="l">
									<Modal.PrimaryFooterButton onPress={handleSave} disabled={pendingRates.length === 0}>
										{formatMessage({id: 'common.save'})}
									</Modal.PrimaryFooterButton>
									<Modal.SecondaryFooterButton onPress={handleClose}>
										{formatMessage({id: 'common.cancel'})}
									</Modal.SecondaryFooterButton>
								</FlexRow>
							</FlexColumn>
							<FlexColumn>
								<FlexRow justifyContent={'flex-end'}>
									{onDelete && (
										<Button
											emphasis={EMPHASIS.MONO}
											borderTop={BORDER.NONE}
											borderRight={BORDER.NONE}
											borderBottom={BORDER.NONE}
											borderLeft={BORDER.NONE}
											icon="trash"
											onPress={onDelete}
										>
											{formatMessage({id: 'settings_finance.delete_exchange_rate_modal_title'})}
										</Button>
									)}
								</FlexRow>
							</FlexColumn>
						</FlexRow>
					</FlexColumn>
				</Modal.Content>
			</Modal>

			{showUnsavedGuard && (
				<Modal size={Modal.SIZE.MEDIUM} onCloseModal={() => setShowUnsavedGuard(false)}>
					<Modal.Title>
						{formatMessage({id: 'settings_finance.currencies.history_modal.unsaved_changes_title'})}
					</Modal.Title>
					<Modal.Content>
						<FlexColumn gap="m">
							<Text>
								{formatMessage({id: 'settings_finance.currencies.history_modal.unsaved_changes_description'})}
							</Text>
							<FlexRow>
								<Modal.PrimaryFooterButton
									onPress={() => {
										setShowUnsavedGuard(false);
										handleSave();
									}}
								>
									<FormattedMessage id="common.save_changes" />
								</Modal.PrimaryFooterButton>
								<Modal.SecondaryFooterButton
									onPress={() => {
										setPendingRates([]);
										setShowUnsavedGuard(false);
										closeModal();
									}}
								>
									<FormattedMessage id="settings_finance.currencies.history_modal.leave_anyway" />
								</Modal.SecondaryFooterButton>
							</FlexRow>
						</FlexColumn>
					</Modal.Content>
				</Modal>
			)}
		</>
	);
};
